import { inject, Injectable } from '@angular/core';
import { from, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { downloadBlob, pdfBytesToBlob } from '../shared/helpers/file';
import type { IArchProfilo, IAttachment } from '../shared/models';
import { ApiService } from './api.service';
import { ArchTemplateService } from './arch-template/arch-template.service';

export interface IExportMeta {
  nomi: string;
  arch: string;
  date: string;
  perc: string;
}

// Download dei documenti da /profile e /rating: tutti PDF, come i file d'archivio di /interviews.
@Injectable({ providedIn: 'root' })
export class ExportService {
  private readonly _archTemplate: ArchTemplateService = inject(ArchTemplateService);
  private readonly _api: ApiService = inject(ApiService);
  private readonly _toast: MessageService = inject(MessageService);

  private get _today(): string {
    return new Date().toLocaleDateString('it-IT');
  }

  // Profilo (Abitativo / Architettonico da markdown) → PDF.
  exportProfile(md: string, subtitle: string, prefix: string, meta: IExportMeta): void {
    this._exportDocumentPdf(md, subtitle, prefix, meta);
  }

  // Profilo Architettonico strutturato → PDF vero (pdf-lib, v. shared/helpers/arch-pdf.ts):
  // un solo motore di rendering, stesso file per pagina (pdf.js) e download. Il template
  // caricato in /admin (se presente) è la base su cui disegnare.
  exportArchProfile(profilo: IArchProfilo, meta: IExportMeta): void {
    this._archTemplate
      .getLatestBytes()
      .pipe(
        // Import dinamico: pdf-lib+fontkit escono dal bundle iniziale (questo service è core,
        // eager) e si scaricano solo al primo download dell'Architettonico.
        switchMap((tpl) =>
          from(
            import('../shared/helpers/arch-pdf').then((m) =>
              m.buildArchPdf(profilo, { templateBytes: tpl }),
            ),
          ),
        ),
      )
      .subscribe((bytes) => {
        downloadBlob(pdfBytesToBlob(bytes), `ProfiloArchitettonico_${this._fileSuffix(meta)}.pdf`);
      });
  }

  // Trascrizione intervista → PDF.
  exportTranscript(transcript: string, meta: IExportMeta): void {
    this._exportDocumentPdf(transcript, 'Trascrizione Intervista Psico-Abitativa', 'Trascrizione', meta);
  }

  // Scheda dell'Abitazione → PDF. doc = scheda caricata (modalità upload), null = modalità form.
  // PDF caricato: si scarica l'originale. Word caricato: PDF dal testo già estratto (lo stesso
  // che legge l'agente). Form: PDF dal testo compilato.
  exportScheda(doc: IAttachment | null, formText: string, meta: IExportMeta): void {
    if (null !== doc && 'application/pdf' === doc.mediaType) {
      downloadBlob(this.attachmentBlob(doc), doc.name);
      return;
    }
    const text = null === doc ? formText : (doc.extractedText ?? '');
    this._exportDocumentPdf(text, "Scheda dell'Abitazione", 'Scheda_Abitazione', meta);
  }

  // Decodifica il base64 di un allegato (es. Scheda caricata come PDF) in Blob, per download o upload.
  attachmentBlob(att: IAttachment): Blob {
    const bytes = atob(att.b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      arr[i] = bytes.charCodeAt(i);
    }
    return new Blob([arr], { type: att.mediaType });
  }

  // Il PDF completo dell'archivio NON si genera qui: lo compone e lo carica il backend
  // (be/proxy-agcasabito/src/pdf/, v. LaTeX sez. "generazione persistita").

  print(): void {
    window.print();
  }

  // Documento singolo renderizzato dal BE (buildDocumentPdf, stesso motore dei file
  // {code}_abitativo.pdf / {code}_scheda.pdf dell'archivio).
  private _exportDocumentPdf(body: string, subtitle: string, prefix: string, meta: IExportMeta): void {
    this._api.documentPdf(subtitle, body, meta).subscribe({
      next: (blob) => downloadBlob(blob, `${prefix}_${this._fileSuffix(meta)}.pdf`),
      error: () => this._toast.add({ severity: 'error', summary: `Download di «${subtitle}» non riuscito.` }),
    });
  }

  private _fileSuffix(meta: IExportMeta): string {
    return `${meta.nomi.replace(/[^a-zA-Z0-9]/g, '_')}_${meta.date || this._today}`;
  }
}
